<template>
    <div>
        <!-- List of users -->
        <q-table
            @request="onRequest"
            class="no-shadow border-grey bg-white"
            :rows="users"
            :columns="table.cols"
            :grid="$q.screen.lt.md"
            :filter="table.search"
            v-model:pagination="table.pagination"
            :rows-per-page-options="[table.pagination.rowsPerPage]"
            :no-data-label="
                $t(
                    'No active user yet. Click on Create User Account to add a user.'
                )
            "
            row-key="id"
            table-style="max-height: 100%;"
            binary-state-sort
            :visible-columns="visibleColumns"
        >
            <!-- top bar -->
            <template v-slot:top>
                <!-- table title -->
                <div class="col-12">
                    <div class="text-h4 text-weight-medium">
                        {{ $t('User Management') }}
                    </div>
                    <div class="page-description">
                        {{
                            $t('Manage user accounts, roles, and permissions.')
                        }}
                    </div>
                </div>
                <div
                    class="row fit"
                    :class="
                        $q.screen.lt.sm
                            ? ''
                            : 'wrap justify-between items-start content-start'
                    "
                >
                    <!-- search bar -->
                    <q-input
                        v-model="table.search"
                        @clear="removeFilteredUserFromStore"
                        :placeholder="$t('Search user')"
                        :class="
                            $q.screen.lt.sm ? 'col-12 q-mb-md' : 'col q-mr-md'
                        "
                        debounce="750"
                        clearable
                        outlined
                        dense
                        for="search-user-input"
                        @input="setFilteredUserInStore"
                        color="accent"
                    >
                        <template v-slot:prepend>
                            <q-icon name="fas fa-search" />
                        </template>
                    </q-input>

                    <!-- 'Create user account' button -->
                    <q-btn
                        id="create-user-btn"
                        @click="
                            $router.push({
                                name: 'user_create'
                            })
                        "
                        :label="$t('Create user account')"
                        v-show="$userCan('create', 'users')"
                        class="table-header__button"
                        :class="$q.screen.lt.sm ? 'col-12' : ''"
                        color="accent"
                        size="md"
                    ></q-btn>
                </div>
            </template>
            <!-- table body -->
            <template v-slot:body="props">
                <q-tr :props="props">
                    <q-td key="role" :props="props">
                        {{ getRoleLabel(props.row.role) }}
                    </q-td>
                    <q-td key="username" :props="props">
                        {{ props.row.username }}
                    </q-td>
                    <q-td key="last_name" :props="props">
                        {{ props.row.last_name }}
                    </q-td>
                    <q-td key="first_name" :props="props">
                        {{ props.row.first_name }}
                    </q-td>
                    <q-td key="email" :props="props">
                        {{ props.row.email }}
                    </q-td>
                    <q-td
                        key="mfa_enabled"
                        :props="props"
                        v-show="$userCan('view', 'mfa_methods')"
                    >
                        <q-icon
                            :name="
                                props.row.mfa_enabled
                                    ? 'fa-solid fa-circle-check'
                                    : 'fa-solid fa-circle-xmark'
                            "
                            :color="
                                props.row.mfa_enabled ? 'green' : 'negative'
                            "
                        />
                    </q-td>
                    <q-td key="job_title" :props="props">
                        {{ props.row.job_title }}
                    </q-td>
                    <q-td key="actions" :props="props">
                        <div class="row q-gutter-md">
                            <!-- "edit user" button -->
                            <q-btn
                                @click="edit(props.row)"
                                class="table__action-button edit-user-btn"
                                color="accent"
                                icon="fas fa-edit"
                                outline
                            />
                            <!-- "remove user" button -->
                            <q-btn
                                @click="remove(props.row)"
                                v-show="$userCan('delete', 'users')"
                                :disable="isCurrentUser(props.row.id)"
                                class="table__action-button delete-user-btn"
                                :color="
                                    isCurrentUser(props.row.id)
                                        ? 'grey'
                                        : 'negative'
                                "
                                icon="fas fa-trash-alt"
                                outline
                            >
                                <q-tooltip v-if="isCurrentUser(props.row.id)">
                                    {{
                                        $t(
                                            'You can only delete your own account from the edit page.'
                                        )
                                    }}
                                </q-tooltip>
                            </q-btn>
                        </div>
                    </q-td>
                </q-tr>
            </template>
            <!-- displayed as card when the screen is smaller-->
            <template v-slot:item="props">
                <div
                    class="q-pa-md col-xs-12 col-sm-6 col-md-4 col-lg-3 grid-style-transition"
                >
                    <q-card>
                        <!-- list of user information -->
                        <q-list dense>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('Role') }}:</strong>
                                        {{ getRoleLabel(props.row.role) }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('Username') }}:</strong>
                                        {{ props.row.username }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('Last name') }}:</strong>
                                        {{ props.row.last_name }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('First name') }}:</strong>
                                        {{ props.row.first_name }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('Email') }}:</strong>
                                        {{ props.row.email }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong
                                            >{{ $t('MFA enabled') }}:</strong
                                        >
                                        <q-icon
                                            class="q-ml-sm"
                                            :name="
                                                props.row.mfa_enabled
                                                    ? 'fa-solid fa-circle-check'
                                                    : 'fa-solid fa-circle-xmark'
                                            "
                                            :color="
                                                props.row.mfa_enabled
                                                    ? 'green'
                                                    : 'negative'
                                            "
                                        />
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                            <q-item>
                                <q-item-section>
                                    <q-item-label>
                                        <strong>{{ $t('Job title') }}:</strong>
                                        {{ props.row.job_title }}
                                    </q-item-label>
                                </q-item-section>
                            </q-item>
                        </q-list>
                        <q-card-section>
                            <!-- "edit user" button -->
                            <q-btn
                                @click="edit(props.row)"
                                class="table__action-button q-mr-sm edit-user-btn"
                                color="primary"
                                icon="fas fa-edit"
                                size="xs"
                                outline
                            />
                            <!-- "remove user" button -->
                            <q-btn
                                @click="remove(props.row)"
                                v-show="$userCan('delete', 'users')"
                                :disable="isCurrentUser(props.row.id)"
                                class="table__action-button"
                                :color="
                                    isCurrentUser(props.row.id)
                                        ? 'grey'
                                        : 'negative'
                                "
                                icon="fas fa-trash-alt"
                                size="xs"
                                outline
                            />
                        </q-card-section>
                    </q-card>
                </div>
            </template>
        </q-table>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { ROLES } from '@models/roles.js';
import { mapActions, mapState } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useFiltersStore } from '@stores/filters.js';
import { useAuthStore } from '@stores/auth';
import { useLanguagesStore } from '@stores/languages.js';

export default {
    name: 'UserList',
    mixins: [BreadcrumbsManager, UpdateURL],
    async created() {
        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Global Settings'),
                route: {
                    name: 'settings'
                }
            },
            {
                label: this.$t('User Management'),
                route: {
                    name: 'user_list'
                }
            }
        ]);

        // get the store filtered user
        const storedFilteredUser = this.getStoredUserAccountFilter({
            facilityId: this.facilityId,
            filter: 'userManagement'
        });

        this.table.search = storedFilteredUser;

        this.onRequest({
            pagination: this.table.pagination,
            filter: ''
        });
    },
    data() {
        return {
            users: [],
            baseURL: 'users/',
            url: 'users/',

            // table configurations
            table: {
                cols: [
                    {
                        name: 'role',
                        required: true,
                        label: this.$t('Role'),
                        field: 'role',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'username',
                        required: true,
                        label: this.$t('Username'),
                        field: 'username',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'last_name',
                        required: true,
                        label: this.$t('Last name'),
                        field: 'last_name',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'first_name',
                        required: true,
                        label: this.$t('First name'),
                        field: 'first_name',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'email',
                        required: true,
                        label: this.$t('Email'),
                        field: 'email',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'mfa_enabled',
                        label: this.$t('MFA enabled'),
                        field: 'mfa_enabled',
                        align: 'center'
                    },
                    {
                        name: 'job_title',
                        label: this.$t('Job title'),
                        field: 'job_title',
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'actions',
                        label: this.$t('Actions'),
                        field: 'actions',
                        align: 'left'
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapState(useFiltersStore, ['getStoredUserAccountFilter']),
        ...mapState(useAuthStore, {
            loggedInUser: 'user'
        }),
        /**
         * Control MFA column visibility according to user role.
         * @return {Array} - visible columns.
         */
        visibleColumns() {
            const columns = [
                'role',
                'username',
                'last_name',
                'first_name',
                'email',
                'job_title',
                'actions'
            ];
            if (this.$userCan('view', 'mfa_methods')) {
                columns.push('mfa_enabled');
            }
            return columns;
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useFiltersStore, ['setUserAccountFilter']),
        ...mapState(useLanguagesStore, ['getLanguages']),
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         * @param {Object} props.pagination
         * @param {string} props.filter
         */
        async onRequest(props) {
            // update pagination object
            this.table.pagination = props.pagination;

            // get current pagination state attributes
            const { descending, page, sortBy } = props.pagination;

            // update URL
            this.url = this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.table.search,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page
                }
            });

            // begin loading users
            this.beginLoading(`${this.$t('Loading users')}...`);
            // clear existing data
            this.users = [];

            // fetch users from the server
            await this.getUsers();
        },
        /**
         * Get users from the server.
         */
        async getUsers() {
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.users.push(...response.data.results);
                    // update total users count
                    this.table.pagination.rowsNumber = response.data.count;
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to load users. Please try again later.'
                        ),
                        error
                    );
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Edit user.
         * @param {Object} row - User from table row
         */
        edit(row) {
            // route to user update page
            // and pass parameters
            this.$router.push({
                name: 'user_update',
                params: {
                    userId: row.id
                },
                state: {
                    username: row.username,
                    email: row.email,
                    phone_number: row.phone_number,
                    first_name: row.first_name,
                    last_name: row.last_name,
                    job_title: row.job_title,
                    report_default_start_time: row.report_default_start_time,
                    default_time_picker: row.default_time_picker,
                    role: row.role,
                    language: row.language
                }
            });
        },
        /**
         * Remove user.
         * @param {Object} row - User from table row
         */
        async remove(row) {
            this.$confirmDelete(
                this.$t('Are you sure you want to remove this user account?')
            ).onOk(async () => {
                // begin loading
                this.beginLoading(`${this.$t('Removing user')}...`);

                await this.$axios
                    .delete(`users/${row.id}/`)
                    .then(response => {
                        // remove user from user list
                        let userIndex = this.users.findIndex(
                            user => user.id === row.id
                        );
                        this.users.splice(userIndex, 1);
                        // notify user
                        this.$notifySuccess(
                            this.$t('The user was successfully removed')
                        );
                    })
                    .catch(e => {
                        const error =
                            e?.response?.data &&
                            typeof e.response.data === 'object'
                                ? e.response.data
                                : {};
                        if (
                            error['error'] &&
                            (error['error'] === 'control_target_role' ||
                                error['error'] === 'control_current_role')
                        ) {
                            this.$notifyError(
                                this.$t(
                                    'You do not have permission to delete a user with this role.'
                                ),
                                error
                            );
                        } else {
                            this.$notifyError(
                                this.$t(
                                    'Failed to remove user account. Please try again later.'
                                ),
                                error
                            );
                        }
                    })
                    .finally(() => {
                        this.stopLoading();
                    });
            });
        },
        /**
         * Create a language object with appropriate format.
         * @param {String} - the value of the language.
         * @return {Object} - language.
         */
        getLanguage(val) {
            // get raw language object from available languages array
            const rawLang = this.getLanguages.find(
                lang => lang['value'] === val
            );
            // create new object
            return {
                label: `${rawLang['label']} / ${rawLang['value']}`,
                value: rawLang['value']
            };
        },
        /**
         * Set the filter input value in the
         * store.
         * Depends on table.search value
         */
        setFilteredUserInStore() {
            this.setUserAccountFilter({
                filter: 'userManagement',
                value: this.table.search
            });
        },
        /**
         * Empty the filter input value in the
         * store
         */
        removeFilteredUserFromStore() {
            this.setUserAccountFilter({
                filter: 'userManagement',
                value: ''
            });
            this.table.search = '';
        },
        /**
         * Get the formatted role.
         * @param {String} role_value - value
         * @return {String} -  role label.
         */
        getRoleLabel(role_value) {
            return ROLES[role_value] || this.$t('Unknown');
        },
        /**
         * Check a given id is the same as the current user id.
         * @return {Boolean} - true if the id is the same.
         */
        isCurrentUser(id) {
            return this.loggedInUser && this.loggedInUser['id'] === id;
        }
    }
};
</script>

<style lang="scss" scoped></style>
